import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { PageContext } from 'components/common/PageContext';
import { PageSpinner } from 'components/atoms/PageSpinner';
import { QueryErrorState } from 'components/common/QueryErrorState';
import { NoTableContent } from 'components/common/NoTableContent';
import { useSessionPlans } from 'hooks/api/backend/session-plan';
import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { SessionPlanListItem } from 'types/SessionPlan';

const PlanListItem = ({ plan }: { plan: SessionPlanListItem }): JSX.Element => {
  const navigate = useNavigate();
  const createdLabel = new Date(plan.createdAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <Box
      as="button"
      textAlign="left"
      w="100%"
      bg="background.primary"
      border="1px solid"
      borderColor="border.accent"
      borderRadius="md"
      p="m"
      cursor="pointer"
      _hover={{ borderColor: 'border.accent.hover', bg: 'background.canvas' }}
      onClick={() => navigate(`${ProtectedRoutes.SESSION_PLAN}/${plan.id}`)}
    >
      <HStack justify="space-between" align="flex-start" gap="m">
        <VStack align="stretch" gap="xs" flex="1" minW={0}>
          <Text textStyle="body.m.medium" color="text.primary" lineClamp={2}>
            {plan.title}
          </Text>
          <Text textStyle="body.s.regular" color="text.secondary" lineClamp={2}>
            {plan.summary}
          </Text>
          <Text textStyle="body.xs.regular" color="text.tertiary">
            {plan.durationMinutes} Min. · Erstellt am {createdLabel}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

export const SessionPlanListPage = (): ReactElement => {
  const navigate = useNavigate();
  const { data: plans, isLoading, isError, error, refetch } = useSessionPlans();

  const handleCreate = (): void => {
    navigate(ProtectedRoutes.SESSION_PLAN_NEW);
  };

  const handleRetry = async (): Promise<void> => {
    try {
      await refetch();
    } catch {
      // QueryErrorState / global handlers cover this
    }
  };

  return (
    <PageContext title="Unterrichtsplaner" headTitle="Unterrichtsplaner">
      <Box bg="background.canvas" minH="100%" px={{ base: 'm', md: 'l' }} py="l">
        <VStack align="stretch" gap="l" maxW="800px">
          <HStack justify="space-between" align="center" gap="m">
            <Text textStyle="body.m.regular" color="text.secondary">
              Deine gespeicherten Unterrichtsentwürfe.
            </Text>
            <Button variant="primary" onClick={handleCreate} flexShrink={0}>
              Neue Planung
            </Button>
          </HStack>

          {isLoading && <PageSpinner />}

          {isError && <QueryErrorState error={error} onRetry={handleRetry} />}

          {!isLoading && !isError && plans && plans.length === 0 && (
            <NoTableContent
              title="Noch keine Unterrichtsplanungen"
              description="Erstelle deinen ersten Unterrichtsentwurf mit KI."
              buttonText="Neue Planung erstellen"
              onClick={handleCreate}
            />
          )}

          {!isLoading && !isError && plans && plans.length > 0 && (
            <VStack align="stretch" gap="s">
              {plans.map((plan) => (
                <PlanListItem key={plan.id} plan={plan} />
              ))}
            </VStack>
          )}
        </VStack>
      </Box>
    </PageContext>
  );
};
